import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { reverseTransaction, fetchReportData } from '../../store/slices/transactionsSlice';
import { reportService } from '../../services/api/reportService';
import { Modal } from '../../components/ui/modal';
import { printTicket, getPrinterStatus, loadPrinterConfig } from '../../services/thermalPrinterService';
import PrinterSetup from '../../components/printer/PrinterSetup';

// ─── Constantes ───────────────────────────────────────────────────────────────

/** type que envía el backend: 1=Recarga 2=Paquete 3=Factura 4=Seguro 5=Todos */
const TABS = [
  { key: 5, label: 'Todos',    color: '#2563eb' },
  { key: 1, label: 'Recargas', color: '#10b981' },
  { key: 2, label: 'Paquetes', color: '#06b6d4' },
  { key: 3, label: 'Facturas', color: '#f59e0b' },
  { key: 4, label: 'Seguros',  color: '#8b5cf6' },
];

const TYPE_LABEL: Record<number, string> = { 1: 'Recarga', 2: 'Paquete', 3: 'Factura', 4: 'Seguro' };
const TYPE_COLORS: Record<number, { bg: string; text: string }> = {
  1: { bg: '#dcfce7', text: '#15803d' },
  2: { bg: '#cffafe', text: '#0e7490' },
  3: { bg: '#fef3c7', text: '#b45309' },
  4: { bg: '#ede9fe', text: '#7c3aed' },
};
const STATUS_LABEL: Record<number | string, string> = {
  0: 'Completada', 1: 'Pendiente', 2: 'Fallida', 3: 'Reversada',
  COMPLETED: 'Completada', PENDING: 'Pendiente', FAILED: 'Fallida', REVERSED: 'Reversada',
};
const STATUS_COLORS: Record<number | string, { bg: string; text: string }> = {
  0: { bg: '#dcfce7', text: '#15803d' },
  1: { bg: '#fef9c3', text: '#854d0e' },
  2: { bg: '#fee2e2', text: '#b91c1c' },
  3: { bg: '#f1f5f9', text: '#475569' },
  COMPLETED: { bg: '#dcfce7', text: '#15803d' },
  PENDING:   { bg: '#fef9c3', text: '#854d0e' },
  FAILED:    { bg: '#fee2e2', text: '#b91c1c' },
  REVERSED:  { bg: '#f1f5f9', text: '#475569' },
};

const fmt = (n: number | string) =>
  new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 })
    .format(Number(n));

const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleString('es-DO', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }) : '—';

/** Convierte fecha YYYY-MM-DD → d/m/Y que espera el backend */
const toBackendDate = (iso: string) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

/** Fecha local en formato YYYY-MM-DD (evita el desfase UTC) */
const localISO = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Hoy en formato YYYY-MM-DD */
const todayISO = () => localISO();

/** 30 días atrás en formato YYYY-MM-DD */
const thirtyDaysAgoISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return localISO(d);
};

// ─── Componente ───────────────────────────────────────────────────────────────

const TransactionsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    reportData = [],
    reportPage = 1,
    reportLastPage = 1,
    reportTotals = null,
    isLoadingReport = false,
    error = null,
  } = useAppSelector((state) => state.transactions as any);

  // ── Filtros ────────────────────────────────────────────────────────────────
  const [activeTab,  setActiveTab]  = useState(5);
  const [searchText, setSearchText] = useState('');
  const [dateFrom,   setDateFrom]   = useState(thirtyDaysAgoISO());
  const [dateTo,     setDateTo]     = useState(todayISO());

  // ── Modals ─────────────────────────────────────────────────────────────────
  const [reverseModal,     setReverseModal]     = useState(false);
  const [selectedRow,      setSelectedRow]      = useState<any>(null);
  const [isReversing,      setIsReversing]      = useState(false);
  const [reverseError,     setReverseError]     = useState<string | null>(null);
  const [loadingTicketId,  setLoadingTicketId]  = useState<number | null>(null);
  const [printerModal,     setPrinterModal]     = useState(false);
  const [printingId,       setPrintingId]       = useState<number | null>(null);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [loadingWaId,      setLoadingWaId]      = useState<number | null>(null);
  const [waModal,          setWaModal]          = useState(false);
  const [waRow,            setWaRow]            = useState<any>(null);
  const [waPhone,          setWaPhone]          = useState('');
  const [waSending,        setWaSending]        = useState(false);
  const [waError,          setWaError]          = useState<string | null>(null);

  // ── Detail modal ───────────────────────────────────────────────────────────
  const [detailModal,      setDetailModal]      = useState(false);
  const [detailRow,        setDetailRow]        = useState<any>(null);
  const [detailData,       setDetailData]       = useState<{key:string;value:string}[]>([]);
  const [detailLoading,    setDetailLoading]    = useState(false);
  const [detailError,      setDetailError]      = useState<string | null>(null);

  // ── Carga de datos ─────────────────────────────────────────────────────────
  const loadReport = useCallback((page = 1) => {
    if (!dateFrom || !dateTo) return;
    dispatch(fetchReportData({
      from_date: toBackendDate(dateFrom),
      to_date:   toBackendDate(dateTo),
      type:      activeTab,
      page,
    }));
  }, [dispatch, dateFrom, dateTo, activeTab]);

  useEffect(() => { loadReport(1); }, [loadReport]);

  // ── Búsqueda local ────────────────────────────────────────────────────────
  const filtered = searchText.trim()
    ? reportData.filter((m: any) => {
        const q = searchText.toLowerCase();
        return (
          (m.operator     || '').toLowerCase().includes(q) ||
          (m.phone_number || '').toLowerCase().includes(q) ||
          (m.xid          || '').toLowerCase().includes(q) ||
          (m.name         || '').toLowerCase().includes(q)
        );
      })
    : reportData;

  // ── Impresora ──────────────────────────────────────────────────────────────
  useEffect(() => {
    loadPrinterConfig();
    const t = setInterval(() => setPrinterConnected(getPrinterStatus().connected), 2000);
    setPrinterConnected(getPrinterStatus().connected);
    return () => clearInterval(t);
  }, []);

  const handlePrint = async (m: any) => {
    setPrintingId(m.id);
    try {
      const res = await reportService.getTransactionDetail(String(m.id));
      const items = res?.data?.data || [];
      await printTicket(items);
    } catch (e: any) {
      alert(e.message || 'Error al imprimir');
    } finally {
      setPrintingId(null);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const openReverseModal = (m: any) => {
    setSelectedRow(m);
    setReverseError(null);
    setReverseModal(true);
  };

  const confirmReverse = async () => {
    if (!selectedRow) return;
    setIsReversing(true);
    setReverseError(null);
    try {
      const result = await dispatch(reverseTransaction(selectedRow.id || selectedRow.xid)).unwrap();
      if (result?.success) {
        loadReport(1);
        setReverseModal(false);
      }
    } catch (err: any) {
      setReverseError(err.message || 'Error al revertir la transacción');
    } finally {
      setIsReversing(false);
    }
  };

  const openDetailModal = async (m: any) => {
    setDetailRow(m);
    setDetailData([]);
    setDetailError(null);
    setDetailModal(true);
    setDetailLoading(true);
    try {
      const res = await reportService.getTransactionDetail(String(m.id));
      if (res.success && res.data) {
        setDetailData(res.data.data || []);
      } else {
        setDetailError(res.message || 'No se pudo cargar el detalle');
      }
    } catch (e: any) {
      setDetailError(e.message || 'Error al cargar el detalle');
    } finally {
      setDetailLoading(false);
    }
  };

  const openWaModal = (m: any) => {
    setWaRow(m);
    setWaPhone(m.phone_number || '');
    setWaError(null);
    setWaModal(true);
  };

  const handleSendWhatsapp = async () => {
    if (!waRow) return;
    const phone = waPhone.replace(/\D/g, '');
    if (phone.length < 10) { setWaError('Ingrese un número válido de 10 dígitos'); return; }
    setWaSending(true);
    setWaError(null);
    try {
      const res = await reportService.sendWhatsappMessage(waRow.xid || String(waRow.id), phone);
      if (res.success) {
        setWaModal(false);
        setWaRow(null);
      } else {
        setWaError(res.message || 'No se pudo enviar el comprobante');
      }
    } catch (e: any) {
      setWaError(e.message || 'Error al enviar por WhatsApp');
    } finally {
      setWaSending(false);
    }
  };

  const handleReprint = async (m: any) => {
    setLoadingTicketId(m.id);
    try {
      const res = await reportService.generateTicket(m.id);
      if (res.success && res.data?.url) {
        window.open(res.data.url, '_blank', 'width=800,height=900');
      } else {
        alert(res.message || 'No se pudo generar el comprobante');
      }
    } catch (e: any) {
      alert(e.message || 'Error al obtener el comprobante');
    } finally {
      setLoadingTicketId(null);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-5 max-w-6xl mx-auto animate-fadeIn">

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center justify-center w-12 h-12 rounded-2xl text-white"
          style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)', boxShadow: '0 4px 16px rgba(37,99,235,0.35)' }}>
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
          </svg>
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-black text-slate-800">Transacciones</h1>
          <p className="text-sm text-slate-500">Historial de operaciones realizadas</p>
        </div>
        {/* Botón impresora */}
        <button type="button" onClick={() => setPrinterModal(true)}
          title={printerConnected ? 'Impresora conectada' : 'Configurar impresora'}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
          style={{
            color: printerConnected ? '#7c3aed' : '#64748b',
            border: `1px solid ${printerConnected ? '#ddd6fe' : '#e2e8f0'}`,
            background: printerConnected ? 'rgba(124,58,237,0.06)' : '#f8fafc',
          }}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
          </svg>
          <span className={`w-2 h-2 rounded-full ${printerConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
        </button>
        <button type="button" onClick={() => loadReport(1)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:bg-slate-100"
          style={{ color: '#2563eb', border: '1px solid #bfdbfe', background: '#eff6ff' }}>
          <svg className={`w-4 h-4 ${isLoadingReport ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          Actualizar
        </button>
      </div>

      {/* Modal configuración impresora */}
      {printerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <PrinterSetup onClose={() => { setPrinterModal(false); setPrinterConnected(getPrinterStatus().connected); }} />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl mb-5"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <p className="text-sm font-semibold text-red-700">{error}</p>
        </div>
      )}

      {/* Totales (cuando hay datos) */}
      {reportTotals && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          {[
            { label: 'Completadas', value: reportTotals.solds,    color: '#10b981', bg: '#f0fdf4' },
            { label: 'Reversadas',  value: reportTotals.reverse,  color: '#f59e0b', bg: '#fffbeb' },
            { label: 'Fallidas',    value: reportTotals.error,    color: '#ef4444', bg: '#fef2f2' },
            { label: 'Total vendido', value: fmt(Number((reportTotals.amount     ?? '0').toString().replace(/,/g, '')) || 0), color: '#2563eb', bg: '#eff6ff', isAmount: true },
            { label: 'Comisiones',    value: fmt(Number((reportTotals.commission ?? '0').toString().replace(/,/g, '')) || 0), color: '#8b5cf6', bg: '#faf5ff', isAmount: true },
          ].map(({ label, value, color, bg }) => (
            <div key={label} className="rounded-2xl px-4 py-3 flex flex-col overflow-hidden" style={{ background: bg, border: `1px solid ${color}22` }}>
              <p className="text-xs font-semibold uppercase tracking-wide truncate" style={{ color }}>{label}</p>
              <p className="font-black mt-0.5 leading-tight truncate" style={{ color, fontSize: 'clamp(0.82rem, 1.5vw, 1.1rem)' }}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Card contenedor */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">

        {/* ── Tabs ──────────────────────────────────────────────────────────── */}
        <div className="border-b border-slate-100 overflow-x-auto">
          <div className="flex min-w-max">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button key={tab.key} type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className="relative flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-colors whitespace-nowrap"
                  style={{ color: isActive ? tab.color : '#94a3b8' }}>
                  {tab.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t"
                      style={{ background: tab.color }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Buscadores ────────────────────────────────────────────────────── */}
        <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center gap-3">

          {/* Búsqueda local de texto */}
          <div className="relative flex-1 min-w-48">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input type="text" value={searchText} onChange={(e) => setSearchText(e.target.value)}
              placeholder="Buscar por operador, número, cliente…"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              style={{ background: '#f8fafc' }} />
          </div>

          {/* Fecha desde */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Desde</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2.5 rounded-xl text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{ background: '#f8fafc' }} />
          </div>

          {/* Fecha hasta */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Hasta</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2.5 rounded-xl text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{ background: '#f8fafc' }} />
          </div>

          {/* Buscar (dispara llamada al backend) */}
          <button type="button" onClick={() => loadReport(1)}
            disabled={isLoadingReport}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60 transition-all"
            style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            Buscar
          </button>
        </div>

        {/* ── Tabla ─────────────────────────────────────────────────────────── */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['XID / Referencia', 'Número / Cliente', 'Operador', 'Tipo', 'Monto', 'Comisión', 'Estado', 'Fecha', 'Acciones'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoadingReport ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center">
                    <div className="flex justify-center items-center gap-3">
                      <svg className="animate-spin h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      <span className="text-sm text-slate-500">Cargando transacciones…</span>
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#f1f5f9' }}>
                        <svg className="w-7 h-7 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-slate-500">No hay transacciones para el período seleccionado</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((m: any, i: number) => {
                  const typeNum    = Number(m.type);
                  const typeColor  = TYPE_COLORS[typeNum]  || { bg: '#f1f5f9', text: '#475569' };
                  const statusColor = STATUS_COLORS[m.status] || { bg: '#f1f5f9', text: '#475569' };
                  const txDate      = m.date ? new Date(m.date) : null;
                  const withinWindow = txDate ? (Date.now() - txDate.getTime()) < 10 * 60 * 1000 : false;
                  const canReverse  = (m.status === 0 || m.status === 'COMPLETED') && withinWindow;
                  // url puede no venir del endpoint de reporte — se construye desde xid
                  const receiptUrl  = m.url || (m.xid ? `https://kaoxet.net/pdf/recharges/recharge-${m.xid}.pdf` : null);
                  const canReprint  = (m.status === 0 || m.status === 'COMPLETED') && !!receiptUrl;

                  return (
                    <tr key={m.id || m.xid || i}
                      onClick={() => openDetailModal(m)}
                      className="border-t border-slate-50 hover:bg-blue-50/40 transition-colors cursor-pointer">

                      {/* XID */}
                      <td className="px-5 py-3">
                        <span className="text-xs font-mono text-slate-500 truncate max-w-32 block">
                          {m.xid || m.id || '—'}
                        </span>
                      </td>

                      {/* Número / Cliente */}
                      <td className="px-5 py-3">
                        <p className="text-sm font-semibold text-slate-800">{m.phone_number || '—'}</p>
                        {(m.name || m.customer_name) && (
                          <p className="text-xs text-slate-400">{m.name || m.customer_name}</p>
                        )}
                      </td>

                      {/* Operador */}
                      <td className="px-5 py-3 text-sm text-slate-600 font-medium">{m.operator || '—'}</td>

                      {/* Tipo */}
                      <td className="px-5 py-3">
                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold"
                          style={{ background: typeColor.bg, color: typeColor.text }}>
                          {TYPE_LABEL[typeNum] || `Tipo ${m.type}`}
                        </span>
                      </td>

                      {/* Monto */}
                      <td className="px-5 py-3 text-sm font-bold text-slate-800 whitespace-nowrap">
                        {m.amount ? fmt(Number(m.amount)) : '—'}
                      </td>

                      {/* Comisión */}
                      <td className="px-5 py-3 text-sm whitespace-nowrap">
                        {m.commission !== undefined ? (
                          <span style={{ color: Number(m.commission) >= 0 ? '#10b981' : '#ef4444' }}>
                            {fmt(Number(m.commission))}
                          </span>
                        ) : '—'}
                      </td>

                      {/* Estado */}
                      <td className="px-5 py-3">
                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold"
                          style={{ background: statusColor.bg, color: statusColor.text }}>
                          {STATUS_LABEL[m.status] ?? `Estado ${m.status}`}
                        </span>
                      </td>

                      {/* Fecha */}
                      <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">
                        {fmtDate(m.date || m.created_at)}
                      </td>

                      {/* Acciones */}
                      <td className="px-5 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          {canReprint && (
                            <button type="button" onClick={() => handleReprint(m)}
                              disabled={loadingTicketId === m.id}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:bg-slate-100 disabled:opacity-50"
                              style={{ color: '#2563eb', border: '1px solid #bfdbfe' }}>
                              {loadingTicketId === m.id
                                ? <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                              }
                              Comprobante
                            </button>
                          )}
                          {canReprint && (
                            <button type="button" onClick={() => openWaModal(m)}
                              disabled={loadingWaId === m.id}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:bg-green-50 disabled:opacity-50"
                              style={{ color: '#16a34a', border: '1px solid #bbf7d0' }}>
                              {loadingWaId === m.id
                                ? <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                : <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                              }
                              WhatsApp
                            </button>
                          )}
                          {/* Imprimir en impresora térmica */}
                          {canReprint && (
                            <button type="button"
                              onClick={() => printerConnected ? handlePrint(m) : setPrinterModal(true)}
                              disabled={printingId === m.id}
                              title={printerConnected ? 'Imprimir en impresora térmica' : 'Conectar impresora'}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                              style={{
                                color: printerConnected ? '#7c3aed' : '#94a3b8',
                                border: `1px solid ${printerConnected ? '#ddd6fe' : '#e2e8f0'}`,
                                background: printerConnected ? 'rgba(124,58,237,0.04)' : 'transparent',
                              }}>
                              {printingId === m.id
                                ? <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/></svg>
                              }
                              {printerConnected ? 'Imprimir' : '🖨️'}
                            </button>
                          )}
                          {canReverse && (
                            <button type="button" onClick={() => openReverseModal(m)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:bg-red-50"
                              style={{ color: '#ef4444', border: '1px solid #fecaca' }}>
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                              </svg>
                              Reversar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer: total mostrado + paginación */}
        {filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3"
            style={{ background: '#f8fafc' }}>
            <p className="text-xs text-slate-400">
              Mostrando <span className="font-bold text-slate-600">{filtered.length}</span> transacciones
              {searchText && ` (filtradas)`}
            </p>
            <div className="flex items-center gap-3">
              {reportPage < reportLastPage && (
                <button type="button"
                  onClick={() => dispatch(fetchReportData({
                    from_date: toBackendDate(dateFrom),
                    to_date:   toBackendDate(dateTo),
                    type:      activeTab,
                    page:      reportPage + 1,
                  }))}
                  disabled={isLoadingReport}
                  className="px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-50 transition-all"
                  style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                  {isLoadingReport ? 'Cargando…' : `Cargar más (pág. ${reportPage}/${reportLastPage})`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Modal Reversión ────────────────────────────────────────────────── */}
      <Modal isOpen={reverseModal} onClose={() => !isReversing && setReverseModal(false)}
        title="Confirmar Reversión" size="sm">
        <div className="py-2">
          {reverseError && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl mb-4"
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <p className="text-sm font-semibold text-red-700">{reverseError}</p>
            </div>
          )}
          <p className="text-sm text-slate-500 mb-4">
            ¿Está seguro que desea revertir esta transacción? Esta acción no se puede deshacer.
          </p>
          {selectedRow && (
            <div className="rounded-xl divide-y divide-slate-100 mb-5 overflow-hidden"
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              {[
                ['XID',      selectedRow.xid],
                ['Número',   selectedRow.phone_number],
                ['Operador', selectedRow.operator],
                ['Monto',    selectedRow.amount ? fmt(Number(selectedRow.amount)) : '—'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs font-medium text-slate-500">{label}</span>
                  <span className="text-sm font-bold text-slate-800">{value}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3">
            <button type="button" onClick={() => setReverseModal(false)} disabled={isReversing}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 hover:bg-slate-50 transition-all disabled:opacity-50">
              Cancelar
            </button>
            <button type="button" onClick={confirmReverse} disabled={isReversing}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50"
              style={{ background: isReversing ? '#94a3b8' : 'linear-gradient(135deg,#ef4444,#dc2626)', boxShadow: '0 4px 12px rgba(239,68,68,0.35)' }}>
              {isReversing ? 'Procesando…' : 'Revertir'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Modal Detalle de Transacción ────────────────────────────────── */}
      <Modal isOpen={detailModal} onClose={() => setDetailModal(false)}
        title="Detalle de Transacción" size="md">
        <div className="py-1">

          {/* Cabecera con tipo / estado */}
          {detailRow && (
            <div className="flex items-center justify-between gap-3 px-1 mb-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold"
                  style={{ background: (TYPE_COLORS[Number(detailRow.type)] || { bg: '#f1f5f9' }).bg, color: (TYPE_COLORS[Number(detailRow.type)] || { text: '#475569' }).text }}>
                  {TYPE_LABEL[Number(detailRow.type)] || `Tipo ${detailRow.type}`}
                </span>
                <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold"
                  style={{ background: (STATUS_COLORS[detailRow.status] || { bg: '#f1f5f9' }).bg, color: (STATUS_COLORS[detailRow.status] || { text: '#475569' }).text }}>
                  {STATUS_LABEL[detailRow.status] ?? `Estado ${detailRow.status}`}
                </span>
              </div>
              <span className="text-xl font-black text-slate-800">
                {detailRow.amount ? fmt(Number(detailRow.amount)) : '—'}
              </span>
            </div>
          )}

          {/* Cuerpo */}
          {detailLoading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <svg className="w-8 h-8 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              <p className="text-sm text-slate-400">Cargando detalle…</p>
            </div>
          ) : detailError ? (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl mb-4"
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <p className="text-sm font-semibold text-red-700">{detailError}</p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden divide-y divide-slate-100 mb-5"
              style={{ border: '1px solid #e2e8f0' }}>
              {detailData.length > 0 ? detailData.map((item, idx) => (
                <div key={idx} className="flex items-start justify-between gap-4 px-4 py-3"
                  style={{ background: idx % 2 === 0 ? '#f8fafc' : '#fff' }}>
                  <span className="text-xs font-semibold text-slate-500 shrink-0 min-w-28">{item.key}</span>
                  <span className="text-sm font-medium text-slate-800 text-right break-all">{item.value || '—'}</span>
                </div>
              )) : (
                /* Fallback: datos básicos del row si el detalle viene vacío */
                [
                  ['XID',       detailRow?.xid],
                  ['Número',    detailRow?.phone_number],
                  ['Operador',  detailRow?.operator],
                  ['Tipo',      TYPE_LABEL[Number(detailRow?.type)] || detailRow?.type],
                  ['Monto',     detailRow?.amount ? fmt(Number(detailRow.amount)) : '—'],
                  ['Comisión',  detailRow?.commission !== undefined ? fmt(Number(detailRow.commission)) : '—'],
                  ['Estado',    STATUS_LABEL[detailRow?.status] ?? detailRow?.status],
                  ['Fecha',     fmtDate(detailRow?.date || detailRow?.created_at)],
                ].map(([label, value], idx) => (
                  <div key={idx} className="flex items-start justify-between gap-4 px-4 py-3"
                    style={{ background: idx % 2 === 0 ? '#f8fafc' : '#fff' }}>
                    <span className="text-xs font-semibold text-slate-500 shrink-0 min-w-28">{label}</span>
                    <span className="text-sm font-medium text-slate-800 text-right break-all">{value || '—'}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Acciones rápidas */}
          {!detailLoading && detailRow && (
            <div className="flex flex-wrap gap-2 pt-1">
              {(detailRow.status === 0 || detailRow.status === 'COMPLETED') && (
                <>
                  <button type="button"
                    disabled={loadingTicketId === detailRow.id}
                    onClick={async () => {
                      setLoadingTicketId(detailRow.id);
                      try {
                        const res = await reportService.generateTicket(detailRow.id);
                        if (res.success && res.data?.url) window.open(res.data.url, '_blank', 'width=800,height=900');
                        else alert(res.message || 'No se pudo generar el comprobante');
                      } catch (e: any) { alert(e.message || 'Error'); }
                      finally { setLoadingTicketId(null); }
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:bg-blue-50 disabled:opacity-50"
                    style={{ color: '#2563eb', border: '1px solid #bfdbfe' }}>
                    {loadingTicketId === detailRow.id
                      ? <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                      : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                    }
                    Comprobante
                  </button>
                  <button type="button"
                    onClick={() => { setDetailModal(false); openWaModal(detailRow); }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:bg-green-50"
                    style={{ color: '#16a34a', border: '1px solid #bbf7d0' }}>
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </button>
                </>
              )}
              {(() => {
                const txDate = detailRow.date ? new Date(detailRow.date) : null;
                const within = txDate ? (Date.now() - txDate.getTime()) < 10 * 60 * 1000 : false;
                return (detailRow.status === 0 || detailRow.status === 'COMPLETED') && within;
              })() && (
                <button type="button"
                  onClick={() => { setDetailModal(false); openReverseModal(detailRow); }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:bg-red-50"
                  style={{ color: '#ef4444', border: '1px solid #fecaca' }}>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                  </svg>
                  Reversar
                </button>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ── Modal WhatsApp ──────────────────────────────────────────────── */}
      <Modal isOpen={waModal} onClose={() => !waSending && setWaModal(false)}
        title="Enviar comprobante por WhatsApp" size="sm">
        <div className="py-2">
          {waError && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl mb-4"
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <p className="text-sm font-semibold text-red-700">{waError}</p>
            </div>
          )}

          {/* Info de la transacción */}
          {waRow && (
            <div className="rounded-xl divide-y divide-slate-100 mb-5 overflow-hidden"
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              {[
                ['XID',      waRow.xid],
                ['Número',   waRow.phone_number],
                ['Operador', waRow.operator],
                ['Monto',    waRow.amount ? fmt(Number(waRow.amount)) : '—'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs font-medium text-slate-500">{label}</span>
                  <span className="text-sm font-bold text-slate-800">{value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Campo de teléfono */}
          <div className="mb-5">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Número WhatsApp (10 dígitos)
            </label>
            <div className="flex items-center rounded-xl overflow-hidden border border-slate-200 focus-within:border-green-400 transition-colors">
              <span className="px-3 py-2.5 text-sm font-semibold text-slate-500 bg-slate-50 border-r border-slate-200">+1</span>
              <input
                type="tel"
                value={waPhone}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '');
                  if (v.length <= 10) { setWaPhone(v); setWaError(null); }
                }}
                placeholder="8091234567"
                className="flex-1 px-3 py-2.5 text-sm outline-none bg-white"
                maxLength={10}
              />
            </div>
            <p className="text-xs text-slate-400 mt-1.5">
              Por defecto se usa el número de la transacción. Puedes cambiarlo.
            </p>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => setWaModal(false)} disabled={waSending}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 hover:bg-slate-50 transition-all disabled:opacity-50">
              Cancelar
            </button>
            <button type="button" onClick={handleSendWhatsapp} disabled={waSending || waPhone.replace(/\D/g,'').length < 10}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50"
              style={{ background: waSending ? '#94a3b8' : 'linear-gradient(135deg,#16a34a,#15803d)', boxShadow: '0 4px 12px rgba(22,163,74,0.35)' }}>
              {waSending
                ? <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Enviando…</>
                : <><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>Enviar WhatsApp</>
              }
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TransactionsPage;
